/* eslint-disable no-console */
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL } from '@ffmpeg/util';

let ffmpeg: FFmpeg | null = null;

export async function getFFmpeg(progressCallback?: (progress: number) => void) {
  if (ffmpeg) {
    if (progressCallback) {
      ffmpeg.on('progress', ({ progress }) => {
        progressCallback(progress);
      });
    }

    return ffmpeg;
  }

  ffmpeg = new FFmpeg();

  if (progressCallback) {
    ffmpeg.on('progress', ({ progress }) => {
      progressCallback(progress);
    });
  }

  const baseUrl = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/esm';
  await ffmpeg.load({
    coreURL: await toBlobURL(`${baseUrl}/ffmpeg-core.js`, 'text/javascript'),
    wasmURL: await toBlobURL(`${baseUrl}/ffmpeg-core.wasm`, 'application/wasm')
  });
  return ffmpeg;
}

const getFileExtension = (fileName: string) => {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex > 0 ? fileName.slice(lastDotIndex + 1) : '';
};

const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for dimension check'));
    };
    img.src = url;
  });
};

export async function blurImageFile(file: File): Promise<File | null> {
  let ffmpegInstance: Awaited<ReturnType<typeof getFFmpeg>> | null = null;
  let inputName: string | null = null;
  let outputName: string | null = null;

  try {
    const { width, height } = await getImageDimensions(file);
    const minDimension = Math.min(width, height);
    const sigma = Math.min(Math.floor(minDimension * 0.1), 1024);

    ffmpegInstance = await getFFmpeg();
    const extension = getFileExtension(file.name) || 'jpg';
    const suffix = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    inputName = `blur-input-${suffix}.${extension}`;
    outputName = `blur-output-${suffix}.jpg`;

    await ffmpegInstance.writeFile(inputName, new Uint8Array(await file.arrayBuffer()));
    await ffmpegInstance.exec(['-y', '-i', inputName, '-vf', `gblur=sigma=${sigma}`, outputName]);

    const data = (await ffmpegInstance.readFile(outputName)) as Uint8Array;
    const blob = new Blob([new Uint8Array(data).buffer], { type: 'image/jpeg' });
    return new File([blob], outputName, { type: 'image/jpeg' });
  } catch (error) {
    console.error('Failed to blur image with FFmpeg:', error);
    return null;
  } finally {
    try {
      if (ffmpegInstance && inputName) {
        await ffmpegInstance.deleteFile(inputName);
      }
      if (ffmpegInstance && outputName) {
        await ffmpegInstance.deleteFile(outputName);
      }
    } catch {
      // Ignore cleanup errors.
    }
  }
}

export async function stripAudioCoverArt(asset: File): Promise<File | null> {
  let ffmpegInstance: Awaited<ReturnType<typeof getFFmpeg>> | null = null;
  let inputName: string | null = null;
  let outputName: string | null = null;

  try {
    ffmpegInstance = await getFFmpeg();
    const assetExtension = getFileExtension(asset.name) || 'audio';
    const suffix = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    inputName = `input-${suffix}.${assetExtension}`;
    outputName = `output-${suffix}.${assetExtension}`;

    await ffmpegInstance.writeFile(inputName, new Uint8Array(await asset.arrayBuffer()));

    await ffmpegInstance.exec([
      '-hide_banner',
      '-i',
      inputName,
      '-map',
      '0:a:0',
      '-c:a',
      'copy',
      outputName
    ]);

    const data = (await ffmpegInstance.readFile(outputName)) as Uint8Array;
    const safeData = new Uint8Array(data);
    const blob = new Blob([safeData.buffer], { type: asset.type || 'audio/mpeg' });
    return new File([blob], asset.name, { type: blob.type });
  } catch (error) {
    console.error('Failed to strip cover art with FFmpeg:', error);
    return null;
  } finally {
    try {
      if (ffmpegInstance && inputName) {
        await ffmpegInstance.deleteFile(inputName);
      }
      if (ffmpegInstance && outputName) {
        await ffmpegInstance.deleteFile(outputName);
      }
    } catch {
      // Ignore cleanup errors.
    }
  }
}
